/* eslint-disable react/react-in-jsx-scope */
import React, { useEffect } from 'react';
import { useCompanyData } from './hooks/useCompanyData';
import { useAuthenticationInit } from './hooks/useAuthenticationInit';
import { useClientConfigInit } from './hooks/useClientConfigInit';
import { useLoginPath } from './hooks/useLoginPath';
import { useResolvedThemeMode } from './hooks/useResolvedThemeMode';
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { themeSettings } from "./theme";
import { RootState, AppDispatch } from "./state/store";
import { useMemo } from "react";
import { HashRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Layout } from "./pages/Layout";
import { Login } from "./pages/Login";
import { LoginConfigError } from "./pages/Login/LoginConfigError";
import { RegisterSuccess } from "./pages/RegisterSuccess/RegisterSuccess";
import { Activate } from "./pages/Activate/Activate";
import { Logger } from "./utils/Logger";
import { LocalStorageAuth } from "./utils/LocalStorageAuth";
import { trackPageView, initInfralytiqs, identifyUser } from "./utils/Infralytiqs";
import { Box, CircularProgress, Backdrop } from '@mui/material';
import { getSideBarMenuItems } from 'SiteMenu';
import { TestSuite } from "./modules/AutomateTest/TestSuite";
import { TestSuiteVersions } from "./modules/AutomateTest";
import { TestCase } from "./modules/AutomateTest/TestCase/TestCase";
import { TestStep } from "./modules/AutomateTest/TestStep";
import { AllTestSuitesReport } from "./modules/AutomateTest/AllTestSuitesReport";
import { Pod } from "./modules/AutomateTest/Pod";
import { AllTestSuiteReportVersions } from "./modules/AutomateTest/AllTestSuitesVersionsReport";
import { AllTestCasesReport } from "./modules/AutomateTest/AllTestCasesReport";
import { AllTestStepsReport } from "./modules/AutomateTest/AllTestStepsReport";
import { CollectionUsers, DataSets, Templates, Runner } from "./modules/InstantUpdate";
import { CampaignReport } from "./modules/InstantUpdate/Runner/CampaignReport";
import { ConsentForm } from "./modules/InstantUpdate/Consent/Public/ConsentForm";
// Import modules
import { Users, Companies } from "./modules/Administration";
import { Security } from "./modules/Settings";
import { BotManager } from "./modules/Settings/BotManager";
import { MessagingQueue } from "./modules/Administration/WorkerPool/MessagingQueue/MessagingQueue";
import { Workers } from "./modules/Administration/WorkerPool/Workers/Workers";
import { ReportSuiteManager } from "./modules/Infralytiqs/Settings/ReportSuiteManager";
import { SuiteReportEditor } from "./modules/Infralytiqs/Settings/SuiteReportEditor";
import { UserDrillDownPage } from "./modules/Infralytiqs/Reports/UserDrillDownPage";
import { SavedReports } from "./modules/Infralytiqs/Reports/SavedReports";
import { Reports } from "./modules/Infralytiqs/Reports";
import { DirectReports, SharedCredentialReports } from "./modules/Infralytiqs/DirectReports";
import { TicketDetail } from "./modules/Support";
import { AdminTicketDetail, SystemNotificationReceivers } from "./modules/Administration";
import { KnowledgeGraphs, AIPrompts } from "./modules/Anamnesis";
import { ManageScreenDetail, OnDemandScreenDetail, ReelEditor as BeamOnScreenReelEditor } from "./modules/BeamOnScreen";

// React Router v6 does not support partial segment params (e.g. /login-:variant).
// This catch-all handles /login-* paths like /login-publicis for custom SSO login pages.
const LoginVariantCatchAll: React.FC<{ user: any; loading: boolean; isInitialized: boolean; loginPath: string }> = ({ user, loading, isInitialized, loginPath }) => {
  const location = useLocation();
  if (location.pathname.startsWith('/login')) {
    return user != null && !loading && isInitialized ? <Navigate to="/dashboard" /> : <Login />;
  }
  return <Navigate to={loginPath} />;
};

// Fires an Infralytiqs `page_view` event on every hash-route change. Lives
// inside <HashRouter> so it can use useLocation(). The SDK auto-attaches
// session_id, anonymous_id and user_id to the event.
const RouteChangeTracker: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + (location.search || '') + (location.hash || ''));
  }, [location.pathname, location.search, location.hash]);
  return null;
};

function App() {
  const { loaded: clientConfigLoaded, data: clientConfigData, loading: clientConfigLoading, retry: retryClientConfig } = useClientConfigInit();
  const loginPath = useLoginPath();

  // Initialize the Infralytiqs tracker as soon as `/system/client/config`
  // resolves. Up until this fires, any track() / identify() calls are
  // captured by the SDK's pre-init buffer and drained here.
  useEffect(() => {
    if (!clientConfigLoaded || !clientConfigData) return;
    const il = clientConfigData.infralytiqs;
    if (!il?.tenantId || !il?.siteId) {
      Logger.debug('st-ck-client/src/App.tsx', 'No infralytiqs bootstrap config for this domain — skipping tracker init');
      return;
    }
    initInfralytiqs({
      serverUrl: clientConfigData.apiBaseUrl,
      tenantId: il.tenantId,
      siteId: il.siteId,
      dbName: il.dbName,
      captureLocation: !!il.captureLocation,
      debug: (clientConfigData.logLevel || '').toLowerCase() === 'debug',
    });
  }, [clientConfigLoaded, clientConfigData]);

  useCompanyData(); // Ensures company info is always in Redux
  const { user, loading, isInitialized } = useAuthenticationInit(); // Use the new authentication hook

  // Re-identify the SDK whenever an authenticated user is available — this
  // covers the session-restore path (token in localStorage, no fresh
  // login flow) and also overwrites any stale identity that was captured
  // before we switched to using email as the canonical user_id.
  useEffect(() => {
    const u = user as unknown as { email?: string; _id?: string; companyId?: string } | null;
    if (!u || !u.email) return;
    identifyUser(u.email, {
      email: u.email,
      user_id: u._id,
      company_id: u.companyId,
    });
  }, [user]);
  
  const themePreference = useSelector((state: RootState) => state.global.mode);
  const mode = useResolvedThemeMode(themePreference);
  const loggerMode = useSelector((state: RootState) => state.logger.value);
  const dispatch = useDispatch<AppDispatch>();
  // Per-domain theme pair (DEFAULT when the domain config omits it).
  const themeName = clientConfigData?.theme || 'DEFAULT';
  const defaultTheme = useMemo(() => createTheme(themeSettings(mode, 'DEFAULT')), [mode]);
  const theme = useMemo(
    () => (clientConfigData ? createTheme(themeSettings(mode, themeName)) : defaultTheme),
    [clientConfigData, defaultTheme, mode, themeName],
  );

  const showClientConfigLoading = !clientConfigLoaded || (clientConfigLoading && !clientConfigData);
  const showClientConfigError = clientConfigLoaded && !clientConfigData;

  const authenticationTokenValue = LocalStorageAuth.getToken();
  // Wait for client config to load first (it's the very first API call), then for
  // authentication (login or session restore). This prevents the HashRouter from
  // unmounting/remounting when data loads, allowing components to update naturally
  // via Redux selectors.
  const isAuthenticating = showClientConfigLoading || (!isInitialized) || (loading && !user);
  
  Logger.debug("st-ck-client/src/App.tsx : App() : user = ", user);
  Logger.debug("st-ck-client/src/App.tsx : App() : user type = ", typeof user);
  Logger.debug("st-ck-client/src/App.tsx : App() : authenticationTokenValue = ", authenticationTokenValue);
  Logger.debug("st-ck-client/src/App.tsx : App() : mode = ", mode);
  Logger.debug("st-ck-client/src/App.tsx : App() : loggerMode = ", loggerMode);
  Logger.debug("st-ck-client/src/App.tsx : App() : loading = ", loading);
  Logger.debug("st-ck-client/src/App.tsx : App() : isInitialized = ", isInitialized);
  Logger.debug("st-ck-client/src/App.tsx : App() : isAuthenticating = ", isAuthenticating);
  
  // Debug: Log authentication state
  useEffect(() => {
    Logger.debug("st-ck-client/src/App.tsx : Current domain:", window.location.hostname);
    Logger.debug("st-ck-client/src/App.tsx : API base URL:", window.location.origin);
    Logger.debug("st-ck-client/src/App.tsx : Authentication token exists:", !!authenticationTokenValue);
  }, [authenticationTokenValue]);

  // Debug: Monitor authentication state changes
  useEffect(() => {
    Logger.debug("st-ck-client/src/App.tsx : Authentication state changed:", {
      user: !!user,
      loading,
      isInitialized,
      isAuthenticating,
      tokenExists: !!authenticationTokenValue
    });
  }, [user, loading, isInitialized, isAuthenticating, authenticationTokenValue]);
  
  // Effect to clear local storage when user becomes null (logout)
  useEffect(() => {
    Logger.debug("st-ck-client/src/App.tsx", `Local storage cleanup effect: user=${user}, authenticationTokenValue=${authenticationTokenValue}, loading=${loading}, isInitialized=${isInitialized}`);
    
    // Only clear local storage if user is explicitly null AND we have a token value
    // AND we're not in the middle of trying to restore a session
    // AND authentication initialization is complete
    if (user === null && authenticationTokenValue !== null && !loading && isInitialized) {
      Logger.debug("st-ck-client/src/App.tsx", "User became null, clearing local storage");
      LocalStorageAuth.clearAll();
    } else if (user === null && authenticationTokenValue === null && isInitialized) {
      Logger.debug("st-ck-client/src/App.tsx", "User is null and no token value - this is normal on page load");
    } else if (user !== null) {
      Logger.debug("st-ck-client/src/App.tsx", "User is authenticated, no need to clear local storage");
    } else if (!isInitialized) {
      Logger.debug("st-ck-client/src/App.tsx", "Authentication not yet initialized, skipping local storage cleanup");
    }
  }, [user, authenticationTokenValue, loading, isInitialized]);





  return (
    <div className="app">
      {showClientConfigError ? (
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <LoginConfigError onRefresh={retryClientConfig} isRetrying={clientConfigLoading} />
        </ThemeProvider>
      ) : isAuthenticating ?
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Backdrop
            sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
            open={isAuthenticating}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </ThemeProvider>
        :
        <HashRouter>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <RouteChangeTracker />
            <Box sx={{ "&& ::-webkit-scrollbar":{width: "5px"},
              "&& ::-webkit-scrollbar-thumb":{backgroundColor: theme.palette.primary.light},
              "&& ::-webkit-scrollbar-track":{backgroundColor: theme.palette.background.paper}
              }}>
              <Routes>
                <Route path="/" element={
                  user != null && !loading && isInitialized ? 
                    <Navigate to="/dashboard" /> : 
                    <Navigate to={loginPath} />
                } />
                <Route element={<Layout />}>
                  {getSideBarMenuItems(clientConfigData?.menuKey).map(({ path, urlKey, icon, module, type, items }) => {
                    if (type === "menuItem") {
                      return <Route key={urlKey} path={path + urlKey} element={user != null ? module?module:icon : <Navigate to={loginPath} />} />
                    } else {
                      return items?.map(({ path, urlKey, icon, module }) => {
                        return <Route key={urlKey} path={path + urlKey} element={user != null ? module?module:icon : <Navigate to={loginPath} />} />
                      })
                    }
                  })}
                  <Route path="/tenant/:tenantId/testSuite/:testSuiteId/testCases/:testCaseId" element={<TestStep />} />
                  <Route path="/tenant/:tenantId/testSuite/:testSuiteId/testCases" element={<TestCase />} />
                  <Route path="/tenant/:tenantId/testSuite/:testSuiteId" element={<TestSuiteVersions />} />
                  <Route path="/tenant/:tenantId" element={<TestSuite />} />
                  <Route path="/report/:tenantId" element={<AllTestSuitesReport />} />
                  <Route path="/report/:reportId/tenant/:tenantId/testSuite/:testSuiteId" element={<AllTestSuiteReportVersions />} />
                  <Route path="/report/:reportId/tenant/:tenantId/testSuite/:testSuiteId/testCases" element={<AllTestCasesReport />} />
                  <Route path="/report/:reportId/tenant/:tenantId/testSuite/:testSuiteId/testCase/:testCaseId" element={<AllTestStepsReport />} />
                  <Route path="/pods" element={<Pod />} />
                  <Route path="/instant-updates/data-sets" element={<DataSets />} />
                  <Route path="/instant-updates/data-sets/:collectionName" element={<CollectionUsers />} />
                  <Route path="/anamnesis/knowledge-graphs/:projectName" element={user != null ? <KnowledgeGraphs /> : <Navigate to={loginPath} />} />
                  <Route path="/anamnesis/ai-prompts/:projectName" element={user != null ? <AIPrompts /> : <Navigate to={loginPath} />} />
                  {/* Beam on Screen → Reel Maker canvas editor (list route comes from the sidebar menu). */}
                  <Route path="/beamonscreen/reel-maker/:reelId" element={user != null ? <BeamOnScreenReelEditor /> : <Navigate to={loginPath} />} />
                  <Route path="/beamonscreen/manage-screens/:screenId" element={user != null ? <ManageScreenDetail /> : <Navigate to={loginPath} />} />
                  <Route path="/beamonscreen/ods/:screenId" element={user != null ? <OnDemandScreenDetail /> : <Navigate to={loginPath} />} />
                  <Route path="/support/my-tickets/:id" element={user != null ? <TicketDetail /> : <Navigate to={loginPath} />} />
                  <Route path="/administration/support-tickets/:id" element={user != null ? <AdminTicketDetail /> : <Navigate to={loginPath} />} />
                  <Route path="/administration/system-notifications/receivers" element={user != null ? <SystemNotificationReceivers /> : <Navigate to={loginPath} />} />
                  <Route path="/instant-updates/templates" element={<Templates />} />
                  <Route path="/instant-updates/runner" element={user != null ? <Runner /> : <Navigate to={loginPath} />} />
                  <Route path="/instant-updates/runner/report/:campaignId" element={user != null ? <CampaignReport /> : <Navigate to={loginPath} />} />
                  {/* New routes for Users and Companies */}
                  <Route path="/settings/users" element={user != null ? <Users /> : <Navigate to={loginPath} />} />
                  <Route path="/settings/companies" element={user != null ? <Companies /> : <Navigate to={loginPath} />} />
                  <Route path="/settings/security" element={user != null ? <Security /> : <Navigate to={loginPath} />} />
                  <Route path="/settings/bot-manager" element={user != null ? <BotManager /> : <Navigate to={loginPath} />} />
                  <Route path="/administration/worker-pool/:poolId/messaging-queue" element={user != null ? <MessagingQueue /> : <Navigate to={loginPath} />} />
                  <Route path="/administration/worker-pool/:poolId/workers" element={user != null ? <Workers /> : <Navigate to={loginPath} />} />
                  <Route path="/infralytiqs/settings/:configId" element={user != null ? <ReportSuiteManager /> : <Navigate to={loginPath} />} />
                  <Route path="/infralytiqs/settings/:configId/suites/:suiteId" element={user != null ? <SuiteReportEditor /> : <Navigate to={loginPath} />} />
                  {/* Saved report-suite shares (IL-ReportSuites-Share) for the active company. */}
                  <Route path="/infralytiqs/reports/:suiteId/saved-reports" element={user != null ? <SavedReports /> : <Navigate to={loginPath} />} />
                  <Route path="/analytics/reports/:suiteId/saved-reports" element={user != null ? <SavedReports /> : <Navigate to={loginPath} />} />
                  {/* Level-1 panel drill-down (deep-linkable by report id). */}
                  <Route path="/infralytiqs/reports/:suiteId/drilldown/:reportId" element={user != null ? <Reports /> : <Navigate to={loginPath} />} />
                  <Route path="/analytics/reports/:suiteId/drilldown/:reportId" element={user != null ? <Reports /> : <Navigate to={loginPath} />} />
                  {/* Level-2 per-user drill-down (parent level-1 report id in path). */}
                  <Route path="/infralytiqs/reports/:suiteId/drilldown/:reportId/user/:userId" element={user != null ? <UserDrillDownPage /> : <Navigate to={loginPath} />} />
                  <Route path="/analytics/reports/:suiteId/drilldown/:reportId/user/:userId" element={user != null ? <UserDrillDownPage /> : <Navigate to={loginPath} />} />
                  {/* Backward-compatible legacy per-user route (no parent panel). */}
                  <Route path="/infralytiqs/reports/:suiteId/:userId" element={user != null ? <UserDrillDownPage /> : <Navigate to={loginPath} />} />
                  <Route path="/analytics/reports/:suiteId/:userId" element={user != null ? <UserDrillDownPage /> : <Navigate to={loginPath} />} />
                </Route>
                
                {/* Public consent route - outside Layout */}
                <Route path="/instant-updates/consent" element={<ConsentForm />} />
                {/* Chrome-less deep-link reports page - outside Layout. The page
                    handles its own SSO bootstrap + auth gate so it can stay
                    header-less with a collapsed sidebar. */}
                <Route path="/infralytiqs/direct-reports" element={<DirectReports />} />
                <Route path="/infralytiqs/shared-reports/:shareId" element={<SharedCredentialReports />} />
                <Route path={loginPath} element={
                  user != null && !loading && isInitialized ? 
                    <Navigate to="/dashboard" /> : 
                    <Login />
                } />
                <Route path="*" element={
                  <LoginVariantCatchAll user={user} loading={loading} isInitialized={isInitialized} loginPath={loginPath} />
                } />
                <Route path="/register-success" element={<RegisterSuccess />} />
                <Route path="/activate" element={<Activate />} />

              </Routes>
            </Box>
          </ThemeProvider>
        </HashRouter>
      }
    </div>
  );
}

export default App;